import { CreditCard, ExternalLink, Trash2, AlertCircle } from "lucide-react";
import { DefaultPaymentPill } from './labels/createPill';
import { EmptyStateText } from './labels/EmptyStateText';
import { ContentCard, EmptyCard } from './layout/ContentCard';
import { IconFrame } from './layout/IconFrame';

export interface PaymentMethod {
  id: string;
  brand?: string;
  last4: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
}

interface PaymentMethodListProps {
  methods: PaymentMethod[];
  onDelete?: (id: string) => void;
  onManage?: () => void;
  error?: boolean;
}

export function PaymentMethodList({
  methods,
  onDelete,
  onManage,
  error,
}: PaymentMethodListProps) {
  if (error) {
    return (
      <div className="space-y-4">
        <div className="relative rounded-xl border border-red-500/20 bg-red-500/5 p-8">
          <EmptyStateText
            icon={
              <IconFrame className="mb-4 h-12 w-12 rounded-full bg-red-500/10">
                <AlertCircle className="h-6 w-6 text-red-400/60" />
              </IconFrame>
            }
            title="Couldn't load your payment methods"
            subtitle="Please try again later"
          />
        </div>
      </div>
    );
  }

  if (methods.length === 0) {
    return (
      <div className="space-y-4">
        <EmptyCard className="relative">
          <EmptyStateText
            icon={
              <IconFrame className="mb-4 h-12 w-12 rounded-full bg-white/[0.03]">
                <CreditCard className="h-6 w-6 text-zinc-500" />
              </IconFrame>
            }
            title="No payment methods"
            subtitle={onManage ? (
              <>
                <button
                  onClick={onManage}
                  className="cursor-pointer underline transition hover:text-zinc-300"
                >
                  Manage payment methods
                </button>{" "}
                via Stripe
              </>
            ) : (
              "Add a payment method to top up your account"
            )}
          />
        </EmptyCard>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Header onManage={onManage} />
      <ContentCard>
        {methods.map((pm, index) => (
          <PaymentMethodRow
            key={pm.id}
            method={pm}
            onDelete={onDelete}
            showBorder={index !== methods.length - 1}
          />
        ))}
      </ContentCard>
    </div>
  );
}

function PaymentMethodInfo({ method }: { method: PaymentMethod }) {
  return (
    <div className="flex items-center gap-3">
      <IconFrame className="h-10 w-14 rounded-lg bg-white/[0.04]">
        <CreditCard className="h-5 w-5 text-zinc-400" />
      </IconFrame>
      <div>
        <div className="font-mono text-[13px] text-zinc-200">
          {method.brand && <span className="capitalize">{method.brand} </span>}
          •••• {method.last4}
        </div>
        {method.expiryMonth != null && method.expiryYear != null && (
          <div className="font-mono text-[11px] text-zinc-500">
            Expires {method.expiryMonth}/{method.expiryYear}
          </div>
        )}
      </div>
    </div>
  );
}

function DeletePaymentButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-zinc-500 transition-colors hover:bg-red-500/10 hover:text-red-400"
      aria-label="Delete payment method"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  );
}

interface PaymentMethodRowProps {
  method: PaymentMethod;
  onDelete?: (id: string) => void;
  showBorder: boolean;
}

function PaymentMethodRow(props: PaymentMethodRowProps) {
  return (
    <div className={`p-4 ${props.showBorder ? "border-b border-white/[0.06]" : ""}`}>
      <div className="flex items-center justify-between">
        <PaymentMethodInfo method={props.method} />
        <div className="flex items-center gap-2">
          {props.method.isDefault && <DefaultPaymentPill label="Default" />}
          {props.onDelete && <DeletePaymentButton onClick={() => props.onDelete!(props.method.id)} />}
        </div>
      </div>
    </div>
  );
}

function Header({ onManage }: { onManage?: () => void }) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-[14px] font-medium text-zinc-200">
          Payment Methods
        </h2>
        {onManage && (
          <button
            onClick={onManage}
            className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/[0.08] px-4 py-1.5 font-mono text-[12px] font-medium text-zinc-400 transition-colors hover:bg-white/[0.03]"
          >
            <ExternalLink className="h-3 w-3" />
            Manage subscription
          </button>
        )}
      </div>
    </div>
  );
}
